import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { getAllSettingsService, updateAdminSetting } from '../services/Settings.service';

const KEY = 'orders-track-from-date';
const TITLE = 'Orders track-from date';

/**
 * Legacy-noise cutoff: orders placed before this date are hidden by default on All Orders and
 * Order Recovery (each screen gets an "Include legacy" toggle). Per-customer views and direct
 * searches always show everything.
 */
const OrderTrackingSettings: React.FC = () => {
  const [date, setDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const settings = await getAllSettingsService();
        const current = settings.find((s: any) => s.id === KEY);
        setDate(String(current?.settingValue || ''));
      } catch {
        // fields stay usable with defaults
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    // Empty = no cutoff, all orders visible (the server accepts it for this key).
    if (date.trim() && !/^\d{4}-\d{2}-\d{2}$/.test(date.trim())) {
      toast.error('Pick a date (YYYY-MM-DD), or clear it to show all orders');
      return;
    }
    setSaving(true);
    try {
      await updateAdminSetting(KEY, TITLE, date.trim());
      toast.success(date.trim() ? `${TITLE} saved` : 'Legacy cutoff cleared — all orders visible');
    } catch (e: any) {
      toast.error(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <h2 className="text-lg font-semibold mb-1">Order Tracking</h2>
      <p className="text-sm text-gray-500 mb-4">
        Hide legacy noise: orders placed before this date are hidden by default on All Orders and Order Recovery
        (each screen has an “Include legacy” toggle). Customer profile orders and direct searches always show
        everything. Dashboard current-state tiles (health, awaiting print, at printer) respect it too.
      </p>
      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1.5 text-sm"
          />
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="bg-storyblue text-white rounded px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
          >
            Save
          </button>
          <span className="text-xs text-gray-400">Clear the date and save to disable the cutoff.</span>
        </div>
      )}
    </div>
  );
};

export default OrderTrackingSettings;
